/* eslint-disable multiline-ternary */
import { Avatar, Badge, Box, Flex, Text, Tooltip } from '@chakra-ui/react'
import { QueueListIcon } from '@heroicons/react/24/outline'
import { Link } from 'react-router-dom'
import { useIntl } from 'react-intl'
import { type QueueEntryType } from '../../model/queue'

interface SidebarQueueProps {
  entries: QueueEntryType[]
  collapsed: boolean
  loading: boolean
  onNavigate?: () => void
}

export default function SidebarQueue({
  entries,
  collapsed,
  loading,
  onNavigate
}: SidebarQueueProps) {
  const { formatMessage } = useIntl()
  const waitingLabel = formatMessage({
    id: 'dashboard.todayQueue.waiting',
    defaultMessage: 'Waiting'
  })
  const emptyLabel = formatMessage({
    id: 'dashboard.todayQueue.noPatientsInQueueToday',
    defaultMessage: 'No patients in queue today'
  })
  const queueCount = loading ? '…' : entries.length

  if (collapsed) {
    const visibleEntries = entries.slice(0, 5)
    const remainingCount = entries.length - visibleEntries.length

    return (
      <Flex
        direction="column"
        align="center"
        flex={1}
        minH={0}
        mt={4}
        data-testid="sidebar-queue"
        aria-label={`${waitingLabel}: ${entries.length}`}
      >
        <Tooltip
          label={
            loading ? `${waitingLabel}…` : `${waitingLabel}: ${entries.length}`
          }
          placement="right"
        >
          <Box position="relative" mb={3} color="gray.500">
            <QueueListIcon width="18px" />
            {entries.length > 0 && (
              <Badge
                position="absolute"
                top="-8px"
                insetInlineEnd="-10px"
                minW="16px"
                h="16px"
                px={1}
                display="flex"
                alignItems="center"
                justifyContent="center"
                borderRadius="full"
                bg="gray.900"
                color="white"
                fontSize="9px"
              >
                {entries.length}
              </Badge>
            )}
          </Box>
        </Tooltip>

        {visibleEntries.map((entry, index) => (
          <Tooltip
            key={entry.id}
            label={`${index + 1}. ${entry.patientName}`}
            placement="right"
          >
            <Box
              as={Link}
              to={`/patients/${entry.patientId}`}
              aria-label={`${index + 1}. ${entry.patientName}`}
              mt={index === 0 ? 0 : '-7px'}
              zIndex={visibleEntries.length - index}
              borderRadius="full"
              onClick={onNavigate}
              transition="transform .15s ease"
              _hover={{ transform: 'translateY(-2px)', zIndex: 10 }}
            >
              <Avatar
                name={entry.patientName}
                size="xs"
                bg="gray.200"
                color="gray.800"
                border="2px solid white"
                fontSize="10px"
              />
            </Box>
          </Tooltip>
        ))}

        {remainingCount > 0 && (
          <Badge mt={1.5} colorScheme="gray" borderRadius="full" fontSize="9px">
            +{remainingCount}
          </Badge>
        )}
      </Flex>
    )
  }

  return (
    <Flex
      direction="column"
      flex={1}
      minH={0}
      mt={4}
      pt={3}
      borderTop="1px solid"
      borderColor="gray.200"
      data-testid="sidebar-queue"
    >
      <Flex align="center" justify="space-between" px={2} mb={2}>
        <Text
          color="gray.500"
          fontSize="10px"
          fontWeight="700"
          letterSpacing="0.08em"
          textTransform="uppercase"
        >
          {waitingLabel}
        </Text>
        <Badge
          minW="20px"
          textAlign="center"
          colorScheme="gray"
          borderRadius="full"
          fontSize="10px"
        >
          {queueCount}
        </Badge>
      </Flex>

      {entries.length === 0 && !loading ? (
        <Text px={2} py={2} color="gray.400" fontSize="xs">
          {emptyLabel}
        </Text>
      ) : (
        <Flex direction="column" gap={0.5} overflowY="auto" px={0.5}>
          {entries.map((entry, index) => (
            <Flex
              key={entry.id}
              as={Link}
              to={`/patients/${entry.patientId}`}
              aria-label={`${index + 1}. ${entry.patientName}`}
              align="center"
              gap={2.5}
              minH="42px"
              px={2}
              py={1.5}
              borderRadius="8px"
              color="gray.700"
              onClick={onNavigate}
              _hover={{ bg: 'gray.100', color: 'gray.950' }}
            >
              <Avatar
                name={entry.patientName}
                size="xs"
                bg="gray.200"
                color="gray.800"
                fontSize="10px"
              />
              <Box minW={0} flex={1}>
                <Text fontSize="xs" fontWeight="600" noOfLines={1}>
                  {entry.patientName}
                </Text>
                <Text color="gray.500" fontSize="10px">
                  #{index + 1}
                </Text>
              </Box>
            </Flex>
          ))}
        </Flex>
      )}
    </Flex>
  )
}
